import ConfirmAction from '@/components/confirm-action';
import StatusPill from '@/components/console/status-pill';
import EmptyState from '@/components/empty-state';
import PageHeader from '@/components/page-header';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDateTime } from '@/lib/format';
import { app_design_path, app_path, app_publication_path } from '@/rails/routes';
import type { AccessCheck, AppShell } from '@/types/platform';
import { Link, router } from '@inertiajs/react';
import { AlertTriangle, ShieldAlert, ShieldCheck } from 'lucide-react';
import { useState } from 'react';

interface Publication {
  url: string;
  publishedAt: string;
  publishedBy: string | null;
  schemaVersion: number;
}

interface Props {
  app: AppShell;
  check: AccessCheck;
  publication: Publication | null;
  databaseMode: 'shared' | 'dedicated' | null;
  canManage: boolean;
}

const SHOWN_BLOCKERS = 3;

const STORE_NOTES = {
  shared:
    'Records for this app are kept in the CAP Rock Labs store, which filters every request by app.',
  dedicated: "Each list was checked against this app's own records store just now.",
} as const;

const Show = ({ app, check, publication, databaseMode, canManage }: Props) => {
  const [busy, setBusy] = useState(false);
  const canPublish = check.passed && canManage;
  const visitOptions = { onStart: () => setBusy(true), onFinish: () => setBusy(false) };

  const publish = () => router.post(app_publication_path(app.id), {}, visitOptions);
  const unpublish = () => router.delete(app_publication_path(app.id), visitOptions);

  // A problem with one list is shown on that list's row; the rest are about the app as a whole.
  // The check names a list by its key. A person knows it by its name, so that is what is shown.
  const rowProblems = new Map(
    check.tables
      .filter((row) => row.problem)
      .map((row) => [`${row.table}: ${row.problem}`, `${row.list ?? row.table}: ${row.problem}`])
  );
  const otherProblems = check.problems.filter((problem) => !rowProblems.has(problem));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Publish"
        description="An app can publish only when every list is locked to this app, so nobody without a login can read it."
        badges={
          publication ? (
            <StatusPill tone="success">Published</StatusPill>
          ) : (
            <StatusPill>Not published</StatusPill>
          )
        }
      />

      {check.passed ? null : (
        <Alert>
          <AlertTriangle className="text-yellow-600" aria-hidden="true" />
          <AlertTitle>Publish is blocked</AlertTitle>
          <AlertDescription>
            <ul className="list-inside list-disc space-y-0.5">
              {check.problems.slice(0, SHOWN_BLOCKERS).map((problem, index) => (
                <li key={index} className="break-words">
                  {rowProblems.get(problem) ?? problem}
                </li>
              ))}
            </ul>
            {check.problems.length > SHOWN_BLOCKERS ? (
              <p>And {check.problems.length - SHOWN_BLOCKERS} more, listed below.</p>
            ) : null}
            {/* Until setup has finished there is nothing in Design that clears the block. */}
            <Link
              href={app.provisioned ? app_design_path(app.id) : app_path(app.id)}
              className="text-foreground font-medium underline underline-offset-4"
            >
              {app.provisioned ? 'Open Design to fix this' : 'Finish setup first'}
            </Link>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{publication ? 'This app is live' : 'This app is not published'}</h2>
          </CardTitle>
          {publication ? (
            <CardDescription>
              Published{publication.publishedBy ? ` by ${publication.publishedBy}` : ''} on{' '}
              {formatDateTime(publication.publishedAt)}, from design version{' '}
              {publication.schemaVersion}.
            </CardDescription>
          ) : (
            <CardDescription>Publishing gives the app its own address.</CardDescription>
          )}
        </CardHeader>

        <CardContent className="space-y-4 text-sm">
          {publication ? (
            <p>
              <span className="text-muted-foreground">Address: </span>
              <a
                href={publication.url}
                target="_blank"
                rel="noreferrer"
                className="font-medium break-all underline underline-offset-4"
              >
                {publication.url}
              </a>
            </p>
          ) : null}

          {publication && app.unpublishedChanges ? (
            <p className="text-yellow-800">
              The design changed since it was published. The live app still shows the earlier
              design.
            </p>
          ) : null}

          {app.watermarked ? (
            <p className="text-muted-foreground">
              On Start, the published app shows a CAP Rock Labs mark.
            </p>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            {publication ? null : (
              <ConfirmAction
                title={`Publish ${app.name}?`}
                description="The app goes live at its own address. Anyone with the address can open its front page. Records stay behind a login."
                confirmLabel="Publish"
                onConfirm={publish}
              >
                <Button disabled={!canPublish || busy}>Publish</Button>
              </ConfirmAction>
            )}

            {publication && app.unpublishedChanges ? (
              <ConfirmAction
                title={`Publish the changes to ${app.name}?`}
                description="The live app switches to the current design."
                confirmLabel="Publish changes"
                onConfirm={publish}
              >
                <Button disabled={!canPublish || busy}>Publish changes</Button>
              </ConfirmAction>
            ) : null}

            {publication && canManage ? (
              <ConfirmAction
                title={`Unpublish ${app.name}?`}
                description="The address stops working until you publish again. Records, customers, and invoices are kept."
                confirmLabel="Unpublish"
                onConfirm={unpublish}
              >
                <Button variant="outline" disabled={busy}>
                  Unpublish
                </Button>
              </ConfirmAction>
            ) : null}
          </div>

          {canManage ? null : (
            <p className="text-muted-foreground">An owner or admin publishes the app.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>Access check</h2>
          </CardTitle>
          <CardDescription>
            {check.passed
              ? 'Every list is locked to this app.'
              : 'Each list has to be protected before the app can publish.'}{' '}
            {databaseMode ? STORE_NOTES[databaseMode] : null}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {check.tables.length === 0 ? (
            <EmptyState description="This app has no lists yet. Add one in Design." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>List</TableHead>
                  <TableHead>Protected</TableHead>
                  <TableHead>Problem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {check.tables.map((row) => (
                  <TableRow key={row.table}>
                    <TableCell className="font-medium">{row.list ?? row.table}</TableCell>
                    <TableCell>
                      {row.ok ? (
                        <span className="inline-flex items-center gap-1.5 text-green-700">
                          <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                          Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-red-700">
                          <ShieldAlert className="h-4 w-4" aria-hidden="true" />
                          No
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="min-w-48 break-words whitespace-normal">
                      {row.problem ?? <span className="text-muted-foreground">—</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {otherProblems.length > 0 ? (
            <div className="text-sm">
              <h3 className="font-medium">Also blocking publish</h3>
              <ul className="mt-1 list-inside list-disc space-y-0.5">
                {otherProblems.map((problem, index) => (
                  <li key={index} className="break-words">
                    {problem}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

export default Show;
